import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';


function BlogDetailsController(props) {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogDetails = async () => {
      setLoading(true);
      try {
        // 1. Fetch single blog details
        const response = await fetch(process.env.REACT_APP_BASE_API_URL + `/get-blog-details?slug=${slug}`);
        const data = await response.json();

        if (data.status === 'success' && data.data && data.data.blog_details) {
          setBlog(data.data.blog_details);
          document.title = `${data.data.blog_details.blog_title} - Emotional Touch`;
        }
      } catch (error) {
        console.warn('API error fetching blog details, using premium fallback mock details:', error);

        setBlog(null);
      } finally {
        setLoading(false);
      }
    };

    const fetchRecentBlogs = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_BASE_API_URL + '/get-blogs-data');
        const data = await response.json();

        if (data.status === 'success' && data.data && data.data.blogs_list) {
          const list = data.data.blogs_list.filter(b => b.blog_slug !== slug).slice(0, 3);
          setRecentBlogs(list);
        }
      } catch (error) {
        console.warn('API error fetching recent blogs, using premium fallback mock details:', error);
      }
    };

    fetchBlogDetails();
    fetchRecentBlogs();
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleCopyLink = () => {
    const el = document.createElement('textarea');
    el.value = window.location.href;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);

    toast.success("Link copied to clipboard!", {
      position: 'top-right',
      autoClose: 2000,
    });
  };

  if (loading) {
    return (
      <main className="blog-details-section">
        <Container className="text-center py-5">
          <Spinner animation="border" variant="danger" className="mb-3" />
          <p>Loading blog content...</p>
        </Container>
      </main>
    );
  }

  if (!blog) {
    return (
      <main className="blog-details-section">
        <Container className="text-center py-5">
          <i className="fas fa-exclamation-triangle fa-3x text-danger mb-3"></i>
          <h2>Blog Post Not Found</h2>
          <p className="text-secondary mb-4">The blog article you are looking for does not exist or has been deleted.</p>
          <Link to="/blogs" className="btn btn-primary btn-rounded px-4">
            Back to Blogs
          </Link>
        </Container>
      </main>
    );
  }

  // Parse tag string to array
  const tagsArray = blog.blog_tag ? blog.blog_tag.split(',').map(tag => tag.trim()) : [];

  return (
    <main>
      <section className="banner_section">
        <Container>
          <div className="page-title">
            <h2>Blog Post</h2>
          </div>
        </Container>
      </section>

      <section className="blog-details-section">
        <Container>
          <Link to="/blogs" className="back-to-blogs-btn">
            <i className="fas fa-arrow-left"></i> Back to Blogs
          </Link>

          <Row>
            {/* Left Content Area */}
            <Col lg={8} md={12}>
              <div className="blog-details-header">
                <div className="blog-details-meta">
                  {blog.blog_category && (
                    <span>
                      <Badge bg="danger">{blog.blog_category}</Badge>
                    </span>
                  )}
                  <span>
                    <i className="far fa-calendar-alt"></i>
                    {formatDate(blog.blog_created_on)}
                  </span>
                  <span>
                    <i className="far fa-user"></i>
                    By {blog.blog_author || "Admin"}
                  </span>
                </div>
                <h1 className="blog-details-title">{blog.blog_title}</h1>
              </div>

              <img
                className="blog-details-featured-img"
                src={blog.blog_image ? blog.blog_image.replace('https:/', 'https://') : 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=800'}
                alt={blog.blog_title}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=800';
                }}
              />

              <div
                className="blog-details-body"
                dangerouslySetInnerHTML={{ __html: blog.blog_description }}
              />
            </Col>

            {/* Sidebar Widget Area */}
            <Col lg={4} md={12} className="mt-5 mt-lg-0">
              {/* Share Widget */}
              <div className="blog-sidebar-widget" id="share-widget">
                <h3 className="blog-sidebar-title">Share Article</h3>
                <p className="text-secondary small">Help friends discover this article by sharing the link.</p>
                <div className="share-buttons">
                  <button className="share-btn" onClick={handleCopyLink} title="Copy Link" id="copy-share-link">
                    <i className="fas fa-link"></i>
                  </button>
                  <a
                    className="share-btn"
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.blog_title)}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Share on Twitter"
                  >
                    <i className="fab fa-twitter"></i>
                  </a>
                  <a
                    className="share-btn"
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Share on Facebook"
                  >
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a
                    className="share-btn"
                    href={`https://api.whatsapp.com/send?text=${encodeURIComponent(blog.blog_title + ' ' + window.location.href)}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Share on WhatsApp"
                  >
                    <i className="fab fa-whatsapp"></i>
                  </a>
                </div>
              </div>

              {/* Recent Blogs Widget */}
              {recentBlogs.length > 0 && (
                <div className="blog-sidebar-widget" id="recent-blogs-widget">
                  <h3 className="blog-sidebar-title">Recent Articles</h3>
                  {recentBlogs.map((recentBlog) => (
                    <div className="recent-blog-item" key={recentBlog.blog_id}>
                      <img
                        className="recent-blog-img"
                        src={recentBlog.blog_image ? recentBlog.blog_image.replace('https:/', 'https://') : 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=150'}
                        alt={recentBlog.blog_title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&q=80&w=150';
                        }}
                      />
                      <div>
                        <Link to={`/blog/${recentBlog.blog_slug}`} className="recent-blog-title">
                          {recentBlog.blog_title}
                        </Link>
                        <div className="recent-blog-date">
                          <i className="far fa-calendar-alt me-1"></i>
                          {formatDate(recentBlog.blog_created_on)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tags Cloud Widget */}
              {tagsArray.length > 0 && (
                <div className="blog-sidebar-widget" id="tags-widget">
                  <h3 className="blog-sidebar-title">Keywords & Tags</h3>
                  <div className="tag-cloud">
                    {tagsArray.map((tag, index) => (
                      <span key={index} className="tag-cloud-item">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
}

export default BlogDetailsController;
